import React, {
  createContext,
  useCallback,
  useState,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import {
  DashboardFilters,
  ReportHubContextValue,
  TimeframeOption,
  ChartGranularity,
  OverviewStats,
  OrderLinePerformance,
  CampaignPerformance,
} from '../Components/ReportHub/types'
import { useAppContext } from './AppContext'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'
import {
  Campaignservicev1Campaign,
  Campaignservicev1OrderLine,
  V1Results,
  V1StatsResponse,
  V1Metrics,
} from 'next-gen-sdk'
import {
  getOrderLineBudget,
  getOrderLineSpent,
} from '../Helpers/order-line-helpers'
import { getOrgFilter } from '../Helpers'
import { FilterValueType } from '@eltoro-ui/components'
import { FilterType } from 'types'
import { ApiFactory } from 'apiFactory'

// Additional types for context
type ExtendedOrderLinePerformance = OrderLinePerformance & {
  impressions?: number
  clicks?: number
}

type ExtendedCampaignPerformance = CampaignPerformance & {
  impressions?: number
  clicks?: number
}

type EnrichedOrderLine = Partial<Campaignservicev1OrderLine> & {
  orgName?: string
  budget?: number
  spent?: number
  ctr?: number
  clicks?: number
  impressions?: number
  calculatedStats?: {
    ctr: number
    budgetServed: number
    budgetTotal: number
  }
  stats?: V1Results
}

type EnrichedCampaign = Partial<Campaignservicev1Campaign> & {
  orgName?: string
  totalOrderLines?: number
  totalBudget?: number
  totalSpent?: number
  ctr?: number
  clicks?: number
  impressions?: number
  calculatedStats?: {
    impressions: number
    clicks: number
    ctr: number
    budgetServed: number
    budgetTotal: number
  }
  stats?: V1Results
}

// Extend dayjs with plugins
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(quarterOfYear)

// Helper function to calculate date range based on timeframe
const calculateDateRange = (
  timeframe: TimeframeOption,
  customDateRange?: { startDate: string; endDate: string },
  campaigns?: Partial<Campaignservicev1Campaign>[],
): { startDate: Date; endDate: Date } => {
  const now = dayjs().tz('America/New_York') // Using EST timezone
  const todayEnd = now.endOf('day').utc().toDate()

  const applyMaxDateConstraint = (endDate: Date): Date => {
    // Ensure end date doesn't go past today
    return endDate.getTime() > todayEnd.getTime() ? todayEnd : endDate
  }

  switch (timeframe) {
    case 'Lifetime':
      // Calculate actual range from campaigns if available
      if (campaigns && campaigns.length > 0) {
        const validCampaigns = campaigns.filter(
          (campaign) => campaign.startTime && campaign.endTime,
        )

        if (validCampaigns.length > 0) {
          const startTimes = validCampaigns.map((campaign) =>
            dayjs(campaign.startTime!),
          )
          const endTimes = validCampaigns.map((campaign) =>
            dayjs(campaign.endTime!),
          )

          // Find earliest start and latest end
          const [firstStartTime] = startTimes
          const [firstEndTime] = endTimes
          let earliestStart = firstStartTime
          let latestEnd = firstEndTime

          for (const startTime of startTimes) {
            if (startTime.isBefore(earliestStart)) {
              earliestStart = startTime
            }
          }

          for (const endTime of endTimes) {
            if (endTime.isAfter(latestEnd)) {
              latestEnd = endTime
            }
          }

          return {
            startDate:
              earliestStart?.utc().toDate() ||
              dayjs('2010-01-01').utc().toDate(),
            endDate: applyMaxDateConstraint(
              latestEnd?.utc().toDate() || now.endOf('day').utc().toDate(),
            ),
          }
        }
      }

      // Fallback to default range if no campaigns available
      return {
        startDate: dayjs('2024-01-01').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }

    case 'Today':
      return {
        startDate: now.startOf('day').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }

    case 'Yesterday':
      const yesterday = now.subtract(1, 'day')
      return {
        startDate: yesterday.startOf('day').utc().toDate(),
        endDate: applyMaxDateConstraint(yesterday.endOf('day').utc().toDate()),
      }

    case 'This Week':
      return {
        startDate: now.startOf('week').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('week').utc().toDate()),
      }

    case 'Last Week':
      const lastWeekStart = now.subtract(1, 'week').startOf('week')
      const lastWeekEnd = now.subtract(1, 'week').endOf('week')
      return {
        startDate: lastWeekStart.utc().toDate(),
        endDate: applyMaxDateConstraint(lastWeekEnd.utc().toDate()),
      }

    case 'MTD':
      return {
        startDate: now.startOf('month').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }

    case 'Last Month':
      const lastMonth = now.subtract(1, 'month')
      return {
        startDate: lastMonth.startOf('month').utc().toDate(),
        endDate: applyMaxDateConstraint(
          lastMonth.endOf('month').utc().toDate(),
        ),
      }

    case 'QTD':
      return {
        startDate: now.startOf('quarter').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }

    case 'Last Quarter':
      const lastQuarter = now.subtract(1, 'quarter')
      return {
        startDate: lastQuarter.startOf('quarter').utc().toDate(),
        endDate: applyMaxDateConstraint(
          lastQuarter.endOf('quarter').utc().toDate(),
        ),
      }

    case 'Custom':
      if (customDateRange?.startDate && customDateRange?.endDate) {
        return {
          startDate: dayjs
            .tz(customDateRange.startDate, 'America/New_York')
            .startOf('day')
            .utc()
            .toDate(),
          endDate: applyMaxDateConstraint(
            dayjs
              .tz(customDateRange.endDate, 'America/New_York')
              .endOf('day')
              .utc()
              .toDate(),
          ),
        }
      }
      // Fallback to today if custom range is incomplete
      return {
        startDate: now.startOf('day').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }

    default:
      return {
        startDate: now.startOf('day').utc().toDate(),
        endDate: applyMaxDateConstraint(now.endOf('day').utc().toDate()),
      }
  }
}

// Helper function to process top performers from stats data
const processTopPerformers = (
  statsData: V1StatsResponse | null,
  orderLines: Partial<Campaignservicev1OrderLine>[],
  campaigns: Partial<Campaignservicev1Campaign>[],
  view: 'campaign' | 'orderLine',
  resolveOrgName: (orgId?: string) => string,
): (ExtendedOrderLinePerformance | ExtendedCampaignPerformance)[] => {
  if (!statsData?.results) return []

  const { results } = statsData
  const topPerformers: (
    | ExtendedOrderLinePerformance
    | ExtendedCampaignPerformance
  )[] = []

  for (const result of results) {
    if (!result.details?.ids) continue

    const impressions = result.totals?.imps || 0
    const clicks = result.totals?.clicks || 0
    const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0

    if (view === 'orderLine') {
      const orderLineId = result?.details?.ids?.orderLineId
      if (!orderLineId) continue

      const orderLine = orderLines.find((ol) => ol.id === orderLineId)
      if (!orderLine) continue

      const cost = getOrderLineSpent(orderLine, impressions)

      // Calculate days left
      const endTime = orderLine.endTime ? dayjs(orderLine.endTime) : null
      const now = dayjs()
      const daysLeft = endTime ? Math.max(0, endTime.diff(now, 'days')) : 0

      topPerformers.push({
        id: orderLineId,
        orderLine: orderLine.name || `Order Line ${orderLineId}`,
        ctr,
        ctrFormatted: `${ctr.toFixed(2)}%`,
        cost,
        costFormatted: `$${cost.toFixed(2)}`,
        daysLeft,
        impressions,
        clicks,
        organizationId: orderLine.orgId,
        organizationName: resolveOrgName(orderLine.orgId),
      })
    } else {
      const campaignId = result?.details?.ids?.campaignId
      if (!campaignId) continue

      const campaign = campaigns.find((c) => c.id === campaignId)
      if (!campaign) continue

      // For campaigns, we need to aggregate cost from all related order lines
      const relatedOrderLines = orderLines.filter(
        (ol) => ol.campaign?.id === campaignId,
      )
      const totalCost = relatedOrderLines.reduce((sum, ol) => {
        const olImpressions = impressions // This would need to be calculated per OL in real implementation
        return sum + getOrderLineSpent(ol, olImpressions)
      }, 0)

      // Calculate average days left for campaign
      const validEndTimes = relatedOrderLines
        .map((ol) => (ol.endTime ? dayjs(ol.endTime) : null))
        .filter(Boolean)

      const now = dayjs()
      const avgDaysLeft =
        validEndTimes.length > 0
          ? Math.max(
              0,
              Math.round(
                validEndTimes.reduce(
                  (sum, endTime) => sum + endTime!.diff(now, 'days'),
                  0,
                ) / validEndTimes.length,
              ),
            )
          : 0

      topPerformers.push({
        id: campaignId,
        campaign: campaign.name || `Campaign ${campaignId}`,
        ctr,
        ctrFormatted: `${ctr.toFixed(2)}%`,
        cost: totalCost,
        costFormatted: `$${totalCost.toFixed(2)}`,
        daysLeft: avgDaysLeft,
        impressions,
        clicks,
        organizationId: campaign.orgId,
        organizationName: resolveOrgName(campaign.orgId),
      })
    }
  }

  // Sort by CTR (highest first) and return top 5
  return topPerformers.sort((a, b) => b.ctr - a.ctr).slice(0, 5)
}

const defaultFilters: DashboardFilters = {
  timeframe: 'Lifetime',
  includeChildOrganizations: false,
  includeAllOrderLines: false,
}

export const ReportHubContext = createContext<
  ReportHubContextValue | undefined
>(undefined)

interface ReportHubProviderProps {
  children: ReactNode
}

export const ReportHubProvider: React.FC<ReportHubProviderProps> = ({
  children,
}) => {
  const [filters, setFilters] = useState<DashboardFilters>(defaultFilters)
  const [overviewStats, setOverviewStats] = useState<OverviewStats | null>(null)
  const [olsAndCampaignsLoading, setOlsAndCampaignsLoading] = useState<boolean>(
    false,
  )
  const [statsLoading, setStatsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string | null>(null)
  const [fetchErrors, setFetchErrors] = useState<{
    orderLines?: string | null
    campaigns?: string | null
    stats?: string | null
  }>({})
  const [view, setView] = useState<'campaign' | 'orderLine'>('orderLine')
  const [chartGranularity, setChartGranularity] = useState<ChartGranularity>(
    'week',
  )
  const [orderLines, setOrderLines] = useState<
    Partial<Campaignservicev1OrderLine>[] | null
  >(null)
  const [campaigns, setCampaigns] = useState<
    Partial<Campaignservicev1Campaign>[] | null
  >(null)
  const [topPerformers, setTopPerformers] = useState<
    (ExtendedOrderLinePerformance | ExtendedCampaignPerformance)[]
  >([])

  const [tableFilter, setTableFilter] = useState<FilterType>()
  const [displayModal, setDisplayModal] = useState<boolean>(false)
  const [
    rawCampaignStats,
    setRawCampaignStats,
  ] = useState<V1StatsResponse | null>(null)
  const [
    rawOrderLineStats,
    setRawOrderLineStats,
  ] = useState<V1StatsResponse | null>(null)
  const [statsProgress, setStatsProgress] = useState<{
    current: number
    total: number
  }>({ current: 0, total: 0 })
  const { currentOrg, timeZone } = useAppContext()

  // Use refs to store latest orderLines and campaigns to avoid dependency issues in callbacks
  const orderLinesRef = useRef<Partial<Campaignservicev1OrderLine>[] | null>(
    null,
  )
  const campaignsRef = useRef<Partial<Campaignservicev1Campaign>[] | null>(null)

  // Refs for tracking merged stats during fetching
  const mergedOrderLineStatsRef = useRef<V1StatsResponse | null>(null)
  const mergedCampaignStatsRef = useRef<V1StatsResponse | null>(null)

  // Update refs whenever state changes
  useEffect(() => {
    orderLinesRef.current = orderLines
  }, [orderLines])

  useEffect(() => {
    campaignsRef.current = campaigns
  }, [campaigns])

  // Calculate date range based on current filters
  const dateRange = useMemo(() => {
    // For 'Lifetime', we need campaigns data to calculate the actual range
    if (filters.timeframe === 'Lifetime' && !campaigns) return null

    return calculateDateRange(
      filters.timeframe,
      filters.customDateRange,
      campaigns || undefined,
    )
  }, [filters.timeframe, filters.customDateRange, campaigns])

  const updateTimeframe = useCallback((timeframe: TimeframeOption) => {
    setFilters((prev) => ({
      ...prev,
      timeframe,
      // Clear custom date range when switching away from Custom
      ...(timeframe !== 'Custom' ? { customDateRange: undefined } : {}),
    }))
  }, [])

  const updateCustomDateRange = useCallback(
    (startDate: string, endDate: string) => {
      setFilters((prev) => ({
        ...prev,
        timeframe: 'Custom',
        customDateRange: { startDate, endDate },
      }))
    },
    [],
  )

  const updateView = useCallback((newView: 'campaign' | 'orderLine') => {
    setView(newView)
  }, [])

  const updateDisplayModal = useCallback((displayModal: boolean) => {
    setDisplayModal(displayModal)
  }, [])

  const updateChartGranularity = useCallback(
    (granularity: ChartGranularity) => {
      setChartGranularity(granularity)
    },
    [],
  )

  const buildParentOrgMap = (
    data: Partial<Campaignservicev1OrderLine | Campaignservicev1Campaign>[],
  ): Record<string, string[]> => {
    const orgMap: Record<string, string[]> = {}

    for (const item of data) {
      if (!item.id || !item.orgId) continue

      if (!orgMap[item.orgId]) {
        orgMap[item.orgId] = []
      }
      orgMap[item.orgId].push(item.id)
    }
    return orgMap
  }

  // Resolve a human-readable org name from orgId for display/filtering
  const resolveOrgName = useCallback(
    (orgId?: string): string => {
      if (!orgId) return ''
      if (currentOrg?.id === orgId) return currentOrg.name || 'Current Org'
      const child = currentOrg?.childOrgs?.find((c) => c.id === orgId)
      if (child) return child.name || `Child Org ${orgId}`
      return orgId || 'Unknown Org'
    },
    [currentOrg],
  )

  // SECOND STEP: Fetch order lines based on filters
  const fetchOrderLines = useCallback(
    async (
      includeAllOrderLines: boolean,
      includeChildOrganizations: boolean,
    ): Promise<Partial<Campaignservicev1OrderLine>[]> => {
      if (!currentOrg?.id) return []

      const allOrderLines: Partial<Campaignservicev1OrderLine>[] = []
      let nextPageToken = ''

      try {
        // Determine which statuses to include
        const states = includeAllOrderLines
          ? ['3', '4', '5', '6', '7', '8'] // Approved, rejected, active, paused, completed, archived
          : ['5'] // active

        // Build filter string
        let filter = ''
        if (states.length > 0) {
          filter = states.map((state) => `state = ${state}`).join(' OR ')
        }

        // Add org filter (including child orgs if needed)
        const orgFilter = getOrgFilter(includeChildOrganizations, currentOrg)

        if (filter && orgFilter) {
          filter = `${filter} AND ${orgFilter}`
        } else if (orgFilter) {
          filter = orgFilter
        }

        do {
          const { campaignServiceAPI } = ApiFactory(
            localStorage.getItem('eltoro_token'),
          )
          if (!campaignServiceAPI) {
            throw new Error('Campaign service API not available')
          }
          const response = await campaignServiceAPI.advertisingPlatformServiceListOrderLines(
            1000, // pageSize
            nextPageToken || undefined,
            undefined, // orderBy
            filter,
          )

          if (!response.orderLines || response.orderLines.length === 0) break

          allOrderLines.push(...response.orderLines)
          nextPageToken = response.nextPageToken || ''
        } while (nextPageToken !== '')

        return allOrderLines
      } catch (error) {
        console.error('Error fetching order lines:', error)
        const errorMessage =
          error instanceof Error
            ? `Failed to fetch order lines: ${error.message}`
            : 'Failed to fetch order lines due to an unknown error'
        setFetchErrors((prev) => ({ ...prev, orderLines: errorMessage }))
        throw new Error(errorMessage)
      }
    },
    [currentOrg],
  )

  // THIRD STEP: Fetch campaigns based on order lines
  const fetchCampaigns = useCallback(
    async (
      orderLinesData: Partial<Campaignservicev1OrderLine>[],
      includeAllOrderLines: boolean,
      includeChildOrganizations: boolean,
    ): Promise<Partial<Campaignservicev1Campaign>[]> => {
      try {
        // Extract unique campaign IDs from order lines
        const uniqueCampaignIds = new Set(
          orderLinesData.map((ol) => ol.campaign?.id).filter(Boolean),
        )

        // Get all campaigns for the organization
        const allCampaigns: Partial<Campaignservicev1Campaign>[] = []
        let nextPageToken = ''

        // Determine which campaign statuses to include
        const campaignStatuses = includeAllOrderLines
          ? ['10', '20', '100'] // Include draft, active, and completed campaigns
          : ['10', '20'] // Only draft and active campaigns

        // Build campaign status filter
        let campaignStatusFilter = ''
        if (campaignStatuses.length > 0) {
          campaignStatusFilter = campaignStatuses
            .map((status) => `status = ${status}`)
            .join(' OR ')
        }

        // Add org filter (including child orgs if needed)
        const orgFilter = getOrgFilter(includeChildOrganizations, currentOrg)

        // Combine filters
        let combinedFilter = ''
        if (campaignStatusFilter && orgFilter) {
          combinedFilter = `${campaignStatusFilter} AND ${orgFilter}`
        } else if (campaignStatusFilter) {
          combinedFilter = campaignStatusFilter
        } else if (orgFilter) {
          combinedFilter = orgFilter
        }

        do {
          const { campaignServiceAPI } = ApiFactory(
            localStorage.getItem('eltoro_token'),
          )
          if (!campaignServiceAPI) {
            throw new Error('Campaign service API not available')
          }
          const response = await campaignServiceAPI.advertisingPlatformServiceListCampaigns(
            1000, // pageSize
            nextPageToken || undefined,
            undefined, // orderBy
            combinedFilter || undefined,
          )

          if (!response.campaigns || response.campaigns.length === 0) break

          allCampaigns.push(...response.campaigns)
          nextPageToken = response.nextPageToken || ''
        } while (nextPageToken !== '')

        // Filter out campaigns that don't belong to the unique campaign ID list
        const filteredCampaigns = allCampaigns.filter((campaign) =>
          uniqueCampaignIds.has(campaign.id),
        )

        return filteredCampaigns
      } catch (error) {
        console.error('Error fetching campaigns:', error)
        const errorMessage =
          error instanceof Error
            ? `Failed to fetch campaigns: ${error.message}`
            : 'Failed to fetch campaigns due to an unknown error'
        setFetchErrors((prev) => ({ ...prev, campaigns: errorMessage }))
        throw new Error(errorMessage)
      }
    },
    [currentOrg],
  )

  //Apply stats data to all fetched data for display in table
  const applyStatsToData = useCallback(
    async (
      orderlineData: EnrichedOrderLine[],
      campaignData: EnrichedCampaign[],
      orderLineStats: V1StatsResponse,
      campaignsStats: V1StatsResponse,
    ): Promise<[EnrichedOrderLine[], EnrichedCampaign[]]> => {
      if (!orderlineData || !campaignData) return [[], []]

      const orderLinesWithStats = orderlineData.map((ol) => {
        const olStats = orderLineStats?.results?.find(
          (result) => result.details?.ids?.orderLineId === ol.id,
        )
        const impressions = olStats?.totals?.imps || 0
        const clicks = olStats?.totals?.clicks || 0
        const budgetTotal = getOrderLineBudget(ol)
        const budgetServed = (impressions * Number(ol?.cpm?.units || 0)) / 1000
        const ctr = impressions > 0 ? (clicks / impressions) * 100 : 0

        return {
          ...ol,
          orgName: resolveOrgName(ol?.orgId),
          calculatedStats: {
            ctr: ctr,
            budgetServed: budgetServed,
            budgetTotal: budgetTotal,
          },
          stats: {
            ...olStats,
          },
        }
      })

      const campaignsWithStats = campaignData.map((campaign) => {
        const campaignStats = campaignsStats?.results?.find(
          (result) => result.details?.ids?.campaignId === campaign.id,
        )

        // For campaigns, aggregate stats from all related order lines
        const relatedOrderLines = orderLinesWithStats.filter(
          (ol) => ol.campaign?.id === campaign.id,
        )

        // Sum budget across related order lines
        const totalBudgetForCampaign = relatedOrderLines.reduce(
          (sum, ol) => sum + getOrderLineBudget(ol),
          0,
        )
        // Sum of the budget served across all related orderlines
        const budgetServedForCampaign = relatedOrderLines.reduce(
          (sum, ol) => sum + ol?.calculatedStats?.budgetServed,
          0,
        )

        // Sum total budgeted impressions across all related order lines
        const totalBudgetedImpressions = relatedOrderLines.reduce(
          (sum, ol) => sum + (ol.impressions || 0),
          0,
        )
        const aggregatedStats = campaignsStats?.results?.reduce(
          (acc: { impressions: number; clicks: number }, result) => {
            if (
              relatedOrderLines.some(
                (ol) => ol.id === result.details?.ids?.orderLineId,
              )
            ) {
              acc.impressions += result.totals?.imps || 0
              acc.clicks += result.totals?.clicks || 0
            }
            return acc
          },
          { impressions: 0, clicks: 0 },
        ) || { impressions: 0, clicks: 0 }

        const servedImpressions =
          campaignStats?.totals?.imps || aggregatedStats.impressions

        const clicks = campaignStats?.totals?.clicks || aggregatedStats.clicks
        const ctr =
          servedImpressions > 0 ? (clicks / servedImpressions) * 100 : 0

        return {
          ...campaign,
          orgName: resolveOrgName(campaign?.orgId),
          calculatedStats: {
            impressions: totalBudgetedImpressions,
            clicks,
            ctr: ctr,
            budgetServed: budgetServedForCampaign,
            budgetTotal: totalBudgetForCampaign,
          },
          stats: {
            ...campaignStats,
          },
        }
      })
      return [orderLinesWithStats, campaignsWithStats]
    },
    [resolveOrgName],
  )

  // Helper function to fetch stats with incremental updates
  const fetchStats = useCallback(
    async (
      config: {
        orderLines: {
          data: Partial<Campaignservicev1OrderLine>[]
          orgMap: Record<string, string[]>
        }
        campaigns: {
          data: Partial<Campaignservicev1Campaign>[]
          orgMap: Record<string, string[]>
        }
      },
      dateRange: { startDate: Date; endDate: Date },
    ) => {
      if (!currentOrg?.id || !dateRange) return

      const timeFrame = 'day'
      const fetchOrgStats = async (
        orgId: string,
        ids: string[],
        type: 'orderLine' | 'campaign',
      ) => {
        if (ids.length === 0) return

        try {
          const statsParams = {
            startTime: dateRange.startDate,
            endTime: dateRange.endDate,
            timezone:
              timeZone &&
              timeZone !== 'AST' &&
              timeZone !== 'ADT' &&
              timeZone !== 'AKST' &&
              timeZone !== 'AKDT'
                ? timeZone
                : 'UTC',
            timeFrame,
            ...(type === 'orderLine'
              ? {
                  levelOfDetail: ['order_line_id'],
                  orderLineId: ids,
                }
              : {
                  levelOfDetail: ['campaign_id'],
                  campaignId: ids,
                }),
          }

          const { statsServiceAPI } = ApiFactory(
            localStorage.getItem('eltoro_token'),
          )
          if (!statsServiceAPI) {
            throw new Error('Stats service API not available')
          }
          const response = await statsServiceAPI.advertisingPlatformServiceGetStats(
            orgId,
            statsParams,
          )
          return response
        } catch (error) {
          console.error('Error fetching stats:', error)
          const errorMessage =
            error instanceof Error
              ? `Failed to fetch ${type} stats: ${error.message}`
              : `Failed to fetch ${type} stats due to an unknown error`
          setFetchErrors((prev) => ({ ...prev, stats: errorMessage }))
          throw new Error(errorMessage)
        }
      }
      const olOrgIds = Object.keys(config.orderLines.orgMap)
      const camOrgIds = Object.keys(config.campaigns.orgMap)
      const allResponses: V1StatsResponse[] = []

      // Priority: currentOrg first, then others
      const priorityOlIds = [
        currentOrg.id,
        ...olOrgIds.filter((id) => id !== currentOrg.id),
      ].filter((id) => config.orderLines.orgMap[id]?.length > 0)

      const priorityCamIds = [
        currentOrg.id,
        ...camOrgIds.filter((id) => id !== currentOrg.id),
      ].filter((id) => config.campaigns.orgMap[id]?.length > 0)

      // Reset merged stats for this fetch
      mergedOrderLineStatsRef.current = null
      mergedCampaignStatsRef.current = null

      // Run both fetch sequences in parallel
      await Promise.all([
        // Sequential OrderLine fetches
        (async () => {
          for (const orgId of priorityOlIds) {
            const ids = config.orderLines.orgMap[orgId]
            const response = await fetchOrgStats(orgId, ids, 'orderLine')
            if (response) {
              allResponses.push(response)
              // Merge stats
              mergedOrderLineStatsRef.current = mergeStatsResponses(
                mergedOrderLineStatsRef.current,
                response,
              )
              setStatsProgress((prev) => {
                return {
                  ...prev,
                  current: prev.current + 1,
                }
              })
              setRawOrderLineStats(mergedOrderLineStatsRef.current)

              // Apply stats incrementally
              const [
                updatedOrderLines,
                updatedCampaigns,
              ] = await applyStatsToData(
                config.orderLines.data as EnrichedOrderLine[],
                config.campaigns.data as EnrichedCampaign[],
                mergedOrderLineStatsRef.current,
                mergedCampaignStatsRef.current || {
                  results: [],
                  totals: {} as V1Metrics,
                },
              )
              setOrderLines(updatedOrderLines)
              setCampaigns(updatedCampaigns)
            }
          }
        })(),

        // Sequential Campaign fetches
        (async () => {
          for (const orgId of priorityCamIds) {
            const ids = config.campaigns.orgMap[orgId]
            const response = await fetchOrgStats(orgId, ids, 'campaign')

            if (response) {
              allResponses.push(response)
              // Merge stats
              mergedCampaignStatsRef.current = mergeStatsResponses(
                mergedCampaignStatsRef.current,
                response,
              )
              setStatsProgress((prev) => {
                return {
                  ...prev,
                  current: prev.current + 1,
                }
              })
              setRawCampaignStats(mergedCampaignStatsRef.current)

              // Apply stats incrementally
              const [
                updatedOrderLines,
                updatedCampaigns,
              ] = await applyStatsToData(
                config.orderLines.data as EnrichedOrderLine[],
                config.campaigns.data as EnrichedCampaign[],
                mergedOrderLineStatsRef.current || {
                  results: [],
                  totals: {} as V1Metrics,
                },
                mergedCampaignStatsRef.current,
              )
              setOrderLines(updatedOrderLines)
              setCampaigns(updatedCampaigns)
            }
          }
        })(),
      ])

      // Reset progress when complete (with 1 second delay)
      setTimeout(() => {
        setStatsProgress(() => ({
          current: 0,
          total: 0,
        }))
      }, 1000)
    },
    [currentOrg, timeZone, applyStatsToData],
  )

  const mergeStatsResponses = (
    existing: V1StatsResponse | null,
    newData: V1StatsResponse,
  ): V1StatsResponse => {
    if (!existing) return newData

    // Merge totals by summing all numeric fields
    const mergedTotals = {} as V1Metrics
    const allKeys = new Set([
      ...Object.keys(existing.totals || {}),
      ...Object.keys(newData.totals || {}),
    ])

    allKeys.forEach((key) => {
      const metricsKey = key as keyof V1Metrics
      const existingValue = existing.totals?.[metricsKey]
      const newValue = newData.totals?.[metricsKey]
      mergedTotals[metricsKey] =
        (typeof existingValue === 'number' ? existingValue : 0) +
        (typeof newValue === 'number' ? newValue : 0)
    })

    return {
      ...existing,
      results: [...(existing.results || []), ...(newData.results || [])],
      totals: mergedTotals,
    }
  }

  // MAIN EFFECT: Refetch everything when currentOrg changes
  useEffect(() => {
    // Reset all state when switching orgs
    setCampaigns(null)
    setOrderLines(null)
    setView('orderLine')
    setRawCampaignStats(null)
    setRawOrderLineStats(null)
    setOverviewStats(null)
    setTopPerformers([])
    setError(null)
    setFetchErrors({})

    if (!currentOrg?.id) return

    const fetchAllData = async () => {
      setOlsAndCampaignsLoading(true)
      setStatsLoading(true)
      setError(null)
      setFetchErrors({})

      try {
        // Fetch order lines
        const orderLinesData = await fetchOrderLines(
          filters.includeAllOrderLines,
          filters.includeChildOrganizations,
        )
        setOrderLines(orderLinesData)

        // Fetch campaigns
        const campaignsData = await fetchCampaigns(
          orderLinesData,
          filters.includeAllOrderLines,
          filters.includeChildOrganizations,
        )
        setCampaigns(campaignsData)
        setOlsAndCampaignsLoading(false)

        // Calculate date range for stats
        const statsDateRange = calculateDateRange(
          filters.timeframe,
          filters.customDateRange,
          campaignsData || undefined,
        )

        // Fetch stats if we have data and valid date range
        if (
          orderLinesData.length > 0 &&
          campaignsData.length > 0 &&
          statsDateRange
        ) {
          const orderLineMap = buildParentOrgMap(orderLinesData)
          const campaignMap = buildParentOrgMap(campaignsData)
          const totalFetches =
            Object.keys(orderLineMap).length + Object.keys(campaignMap).length
          // Initialize progress tracking
          setStatsProgress({ current: 0, total: totalFetches })

          setStatsLoading(false)

          await fetchStats(
            {
              orderLines: { data: orderLinesData, orgMap: orderLineMap },
              campaigns: { data: campaignsData, orgMap: campaignMap },
            },
            statsDateRange,
          )
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch data'
        setError(errorMessage)
        console.error('Error in fetchAllData:', err)
      } finally {
        setOlsAndCampaignsLoading(false)
        setStatsLoading(false)
      }
    }

    fetchAllData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id])

  // Handler for filter changes (includeChildOrganizations, includeAllOrderLines)
  const handleFilterChange = useCallback(
    async (newFilters: Partial<DashboardFilters>) => {
      // Merge new filters with current filters to get the complete set
      const updatedFilters = { ...filters, ...newFilters }

      // Update filters state
      setFilters(updatedFilters)

      if (!currentOrg?.id) return

      setOlsAndCampaignsLoading(true)
      setStatsLoading(true)
      setError(null)
      setFetchErrors({})

      try {
        // Fetch order lines with new filters - use the updated filter values
        const orderLinesData = await fetchOrderLines(
          updatedFilters.includeAllOrderLines,
          updatedFilters.includeChildOrganizations,
        )
        setOrderLines(orderLinesData)

        // Fetch campaigns
        const campaignsData = await fetchCampaigns(
          orderLinesData,
          updatedFilters.includeAllOrderLines,
          updatedFilters.includeChildOrganizations,
        )
        setCampaigns(campaignsData)
        setOlsAndCampaignsLoading(false)

        // Calculate date range for stats
        const statsDateRange = calculateDateRange(
          updatedFilters.timeframe,
          updatedFilters.customDateRange,
          campaignsData || undefined,
        )

        // Fetch stats if we have data and valid date range
        if (
          orderLinesData.length > 0 &&
          campaignsData.length > 0 &&
          statsDateRange
        ) {
          const orderLineMap = buildParentOrgMap(orderLinesData)
          const campaignMap = buildParentOrgMap(campaignsData)
          const totalFetches =
            Object.keys(orderLineMap).length + Object.keys(campaignMap).length
          // Initialize progress tracking
          setStatsProgress({ current: 0, total: totalFetches })

          await fetchStats(
            {
              orderLines: { data: orderLinesData, orgMap: orderLineMap },
              campaigns: { data: campaignsData, orgMap: campaignMap },
            },
            statsDateRange,
          )
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch data'
        setError(errorMessage)
        console.error('Error in handleFilterChange:', err)
      } finally {
        setOlsAndCampaignsLoading(false)
        setStatsLoading(false)
      }
    },
    [currentOrg, filters, fetchOrderLines, fetchCampaigns, fetchStats],
  )

  // Handler for timeframe changes (only refetch stats)
  const handleTimeframeChange = useCallback(
    async (
      timeframe: TimeframeOption,
      customDateRange?: { startDate: string; endDate: string },
    ) => {
      // Update filters
      setFilters((prev) => ({
        ...prev,
        timeframe,
        customDateRange:
          timeframe === 'Custom'
            ? customDateRange || prev.customDateRange
            : undefined,
      }))

      // Use refs to get current values without adding them as dependencies
      const currentOrderLines = orderLinesRef.current
      const currentCampaigns = campaignsRef.current

      // If we don't have order lines and campaigns yet, nothing to do
      if (!currentOrderLines || !currentCampaigns || !currentOrg?.id) return

      setStatsLoading(true)
      setError(null)

      try {
        // Calculate new date range
        const statsDateRange = calculateDateRange(
          timeframe,
          customDateRange,
          currentCampaigns || undefined,
        )

        if (!statsDateRange) {
          setStatsLoading(false)
          return
        }

        if (currentOrderLines.length === 0 && currentCampaigns.length === 0) {
          setStatsLoading(false)
          return
        }
        const orderLineMap = buildParentOrgMap(currentOrderLines)
        const campaignMap = buildParentOrgMap(currentCampaigns)
        const totalFetches =
          Object.keys(orderLineMap).length + Object.keys(campaignMap).length
        // Initialize progress tracking
        setStatsProgress({ current: 0, total: totalFetches })

        // Fetch only stats with the new timeframe
        await fetchStats(
          {
            orderLines: { data: currentOrderLines, orgMap: orderLineMap },
            campaigns: { data: currentCampaigns, orgMap: campaignMap },
          },
          statsDateRange,
        )
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Failed to fetch stats'
        setError(errorMessage)
        setFetchErrors((prev) => ({ ...prev, stats: errorMessage }))
        console.error('Error in handleTimeframeChange:', err)
      } finally {
        setStatsLoading(false)
      }
    },
    [currentOrg, fetchStats],
  )

  // Fetch stats when timeline changes OR when order lines/campaigns become available
  useEffect(() => {
    if (!orderLines || !campaigns || !rawOrderLineStats || !rawCampaignStats) {
      return
    }

    // Use the appropriate stats data based on the current view
    const statsData = view === 'campaign' ? rawCampaignStats : rawOrderLineStats

    // Calculate overview stats for the current view
    const activeOrderLinesData = orderLines.filter(
      (ol) => ol.state === 'ORDERLINE_STATE_ACTIVE',
    )
    const activeOrderLines = activeOrderLinesData.length

    const activeCampaignIds = new Set(
      activeOrderLinesData.map((ol) => ol.campaign?.id).filter(Boolean),
    )
    const activeCampaigns = activeCampaignIds.size

    const totalImpressions = statsData?.totals?.imps || 0
    const totalClicks = statsData?.totals?.clicks || 0
    const ctr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0

    const totalBudget = orderLines.reduce((sum, orderLine) => {
      return sum + getOrderLineBudget(orderLine)
    }, 0)

    const totalSpent = (() => {
      const totalOrderLineImpressions = orderLines.reduce(
        (total, ol) => total + (ol.impressions || 0),
        0,
      )
      const spent =
        ((totalImpressions || 0) / (totalOrderLineImpressions || 1)) *
        totalBudget
      if (spent > totalBudget) return totalBudget
      return spent
    })()

    setOverviewStats({
      totalImpressions,
      budget: { current: totalSpent, total: totalBudget },
      ctr: { current: ctr },
      activeCampaigns,
      activeOrderLines,
      totalClicks,
      statsData,
    })

    // Recalculate top performers for the new view
    const topPerformersData = processTopPerformers(
      statsData,
      orderLines,
      campaigns,
      view,
      resolveOrgName,
    )
    setTopPerformers(topPerformersData)
  }, [
    view,
    orderLines,
    campaigns,
    rawOrderLineStats,
    rawCampaignStats,
    resolveOrgName,
  ])

  // Optimized date filtering with early returns and cached date calculations
  const calculatedFilteredData = useCallback(
    (
      data: (
        | Partial<Campaignservicev1Campaign>
        | Partial<Campaignservicev1OrderLine>
      )[],
      dateRange: { startDate: Date; endDate: Date },
    ) => {
      // Early return for empty data
      if (!data || data.length === 0) return []

      // Cache date range values to avoid repeated property access
      const rangeStart = dateRange.startDate.getTime()
      const rangeEnd = dateRange.endDate.getTime()

      // Optimized filter with cached date parsing and early returns
      return data.filter((item) => {
        if (!item || !item['startTime'] || !item['endTime']) return false

        // Parse dates only once and cache the result
        const itemStartTime = new Date(item.startTime).getTime()
        const itemEndTime = new Date(item.endTime).getTime()

        // Use cached timestamp comparisons for better performance
        return itemEndTime >= rangeStart && itemStartTime < rangeEnd
      })
    },
    [],
  )

  // Memoized helper: safely get nested value by dot path (e.g., 'campaign.name')
  const getByPath = useCallback(
    (obj: Record<string, unknown>, path: string) =>
      path
        .split('.')
        .reduce(
          (acc: unknown, key: string) =>
            acc && typeof acc === 'object' && key in acc
              ? (acc as Record<string, unknown>)[key]
              : undefined,
          obj,
        ),
    [],
  )

  // Optimized table filtering with pre-computed filter entries and reduced string operations
  const applyTableFilters = useCallback(
    <T extends Record<string, unknown>>(rows: T[]) => {
      if (!tableFilter) return rows

      // Pre-compute and cache filter entries
      const filterEntries = Object.entries(tableFilter).filter(
        ([, vals]) => Array.isArray(vals) && vals.length > 0,
      )

      if (filterEntries.length === 0) return rows

      // Pre-process filter values for performance
      const processedFilters = filterEntries.map(([key, vals]) => ({
        key,
        hasPath: key.includes('.'),
        values: (vals as Array<string | number>).map((val) => ({
          original: val,
          isNumber: typeof val === 'number',
          lowercase:
            typeof val !== 'number' ? val.toString().toLowerCase() : null,
        })),
      }))

      return rows.filter((row) => {
        return processedFilters.every(({ key, hasPath, values }) => {
          const rowVal = hasPath ? getByPath(row, key) : row[key]

          // Early return for undefined/null values
          if (rowVal == null) return false

          // Convert to string safely
          const rowValStr = String(rowVal).toLowerCase()

          return values.some(({ original, isNumber, lowercase }) => {
            // Optimized comparison: numeric equality first, then string matching
            return isNumber
              ? Number(rowVal) === original
              : rowValStr.includes(lowercase!)
          })
        })
      })
    },
    [tableFilter, getByPath],
  )

  // Filter data being passed into table based on timeframe and table filters
  const { filteredOlData, filteredCampaignData } = useMemo(() => {
    if (!orderLines || !campaigns || !dateRange) {
      return { filteredOlData: [], filteredCampaignData: [] }
    }

    // Apply date-range filter first
    const validOrderLines = calculatedFilteredData(
      orderLines,
      dateRange,
    ) as Partial<Campaignservicev1OrderLine>[]

    const validCampaigns = calculatedFilteredData(
      campaigns,
      dateRange,
    ) as Partial<Campaignservicev1Campaign>[]

    const olFiltered = applyTableFilters(validOrderLines)
    const camFiltered = applyTableFilters(validCampaigns)

    return { filteredOlData: olFiltered, filteredCampaignData: camFiltered }
  }, [
    dateRange,
    orderLines,
    campaigns,
    applyTableFilters,
    calculatedFilteredData,
  ])
  const handleTableFilter = (
    _: number,
    path: string | string[],
    filterOn: FilterValueType,
  ) => {
    const key = typeof path === 'object' ? path.join('.') : path

    setTableFilter((prev) => {
      return { ...prev, [key]: filterOn }
    })
  }

  // Clears all applied table filters and restores filtered data to current timeframe results
  const clearTableFilters = () => {
    setTableFilter(undefined)
  }

  // Clear all errors
  const clearErrors = useCallback(() => {
    setError(null)
    setFetchErrors({})
  }, [])

  // Consolidated loading state - true if ANY loading is happening or initial load not complete
  const isLoading = olsAndCampaignsLoading || statsLoading

  // Consolidated error state - combines general error and specific fetch errors
  const hasErrors = Boolean(
    error ||
      fetchErrors.orderLines ||
      fetchErrors.campaigns ||
      fetchErrors.stats,
  )

  const allErrors = [
    error,
    fetchErrors.orderLines,
    fetchErrors.campaigns,
    fetchErrors.stats,
  ].filter(Boolean) as string[]

  const contextValue: ReportHubContextValue = {
    filters,
    dateRange,
    overviewStats,
    topPerformers,
    olsAndCampaignsLoading,
    statsLoading,
    isLoading,
    error,
    hasErrors,
    allErrors,
    statsProgress,
    view,
    chartGranularity,
    orderLines,
    campaigns,
    filteredOlData,
    filteredCampaignData,
    displayModal,
    updateTimeframe,
    updateCustomDateRange,
    updateView,
    updateChartGranularity,
    updateDisplayModal,
    handleFilterChange,
    handleTimeframeChange,
    handleTableFilter,
    clearTableFilters,
    clearErrors,
  }

  return (
    <ReportHubContext.Provider value={contextValue}>
      {children}
    </ReportHubContext.Provider>
  )
}
